import React, { useState, useEffect } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import ImpersonationService from '../services/ImpersonationService';
import MeService from '../services/MeService';
import EntitlementsService from '../services/EntitlementsService';

export interface EndOnBehalfOfButtonProps {
    onSessionEnd?: () => void;
}

async function endOnBehalfOfSession(onSessionEnd?: () => void): Promise<void> {
    ImpersonationService.clearSession();
    MeService.clearMe();
    try {
        await Promise.all([
            MeService.fetchMe(),
            EntitlementsService.fetchEntityEntitlements(true),
        ]);
    } catch {
        // best-effort
    }
    onSessionEnd?.();
}

/**
 * Red X button shown in the header (left of user badge, right of theme selector) when
 * on-behalf-of is active. Tooltip explains that clicking ends the impersonation session.
 */
export const EndOnBehalfOfButton: React.FC<EndOnBehalfOfButtonProps> = ({ onSessionEnd }) => {
    const [active, setActive] = useState(() => ImpersonationService.getIsOnBehalfOfEffective());

    useEffect(() => {
        const sync = () => setActive(ImpersonationService.getIsOnBehalfOfEffective());
        return ImpersonationService.subscribeOnBehalfOf(sync);
    }, []);

    if (!active) return null;

    const upn = ImpersonationService.getOnBehalfOfUpn();
    const tooltipLabel = upn
        ? `End viewing as ${upn} (stop on-behalf-of session)`
        : 'End on-behalf-of session';

    return (
        <Tooltip label={tooltipLabel} withArrow>
            <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                aria-label={tooltipLabel}
                onClick={() => endOnBehalfOfSession(onSessionEnd)}
            >
                <IconX size={18} stroke={2.5} />
            </ActionIcon>
        </Tooltip>
    );
};
